import is from '@sindresorhus/is';
import clsx from 'clsx';
import type { FunctionComponent } from 'react';
import { useEffect } from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import { Button } from '~/ui/core/button/Button';
import { IconCrossSmall } from '~/ui/core/icons';

import { useActiveThreadStore } from './ActiveThreadStoreProvider';
import { CommentLanguagesMenu } from './CommentLanguagesMenu';
import { CommentsRail } from './CommentsRail';
import { CommentsViewModeSelector } from './CommentsViewModeSelector';
import type { CommentClassification, HiddenCommentGroup } from './comment-classification';
import { isCommentsScopeCurrent, useCommentsScope } from './comments-scope-context';
import { HiddenCommentsList } from './HiddenCommentsList';
import type { CommentLanguageOption } from './types';
import { useOtherLanguagesWithComments } from './use-other-languages-with-comments';

const EMPTY_HIDDEN_GROUPS: readonly HiddenCommentGroup[] = [];

type CommentsSectionProps = {
    readonly architectureId: string;
    readonly availableLanguages: readonly CommentLanguageOption[];
    readonly commentResolutionDisabled?: boolean;
    readonly itemId: string;
    readonly version: number;
    readonly language: string;
    readonly newThreadCreationDisabled?: boolean;
    readonly onClose: () => void;
    readonly onSwitchLanguage: (languageId: string) => void;
};

export const CommentsSection: FunctionComponent<CommentsSectionProps> = ({
    architectureId,
    availableLanguages,
    commentResolutionDisabled,
    itemId,
    language,
    newThreadCreationDisabled,
    onClose,
    onSwitchLanguage,
    version,
}) => {
    const translate = useTranslate();
    const scope = useCommentsScope();
    const viewMode = useActiveThreadStore((state) => state.viewMode);
    const setViewMode = useActiveThreadStore((state) => state.setViewMode);
    const commentClassification = useActiveThreadStore<CommentClassification | null>((state) =>
        isCommentsScopeCurrent(state.commentClassificationScope, scope) ? state.commentClassification : null,
    );
    const activeCount = commentClassification?.activeComments.length ?? 0;
    const hiddenCount = commentClassification?.hiddenCount ?? 0;
    const hiddenGroups = commentClassification?.hiddenGroups ?? EMPTY_HIDDEN_GROUPS;
    const otherLanguages = useOtherLanguagesWithComments({
        architectureId,
        availableLanguages,
        currentLanguage: language,
        itemId,
        version,
    });
    const hasControls = is.nonEmptyArray(otherLanguages) || hiddenCount > 0;

    useEffect(() => {
        if (viewMode !== 'hidden' || hiddenCount > 0) {
            return;
        }
        const returnToActiveTimeout = window.setTimeout(() => {
            setViewMode('active');
        }, 0);
        return () => window.clearTimeout(returnToActiveTimeout);
    }, [hiddenCount, setViewMode, viewMode]);

    return (
        <aside
            aria-label={translate('pages.job-architecture-v2.comments.section.title')}
            className="relative h-full w-[340px] shrink-0 pb-m pr-m font-sans"
            data-testid="comments-section"
        >
            <div
                className={clsx('sticky top-0 z-10 bg-gray-50', hasControls ? undefined : 'border-b border-gray-200')}
                data-testid="comments-section-sticky-header"
            >
                <header className="flex items-center justify-between gap-s px-m pt-xxl pb-s">
                    <span className="shrink-0 text-xs font-medium tracking-tight text-black-700">
                        {translate('pages.job-architecture-v2.comments.section.title')}
                    </span>
                    <Button
                        aria-label={translate('pages.job-architecture-v2.comments.section.close-label')}
                        className="!h-[24px] !w-[24px] !p-0 [&>svg]:!h-[10px] [&>svg]:!w-[10px]"
                        data-testid="comments-section-close"
                        iconOnly={true}
                        size="sm"
                        variant="secondary"
                        onClick={onClose}
                    >
                        <IconCrossSmall />
                    </Button>
                </header>
                {hasControls ? (
                    <div
                        className="flex min-w-0 flex-wrap items-center gap-s border-b border-gray-200 px-m pb-s"
                        data-testid="comments-section-controls"
                    >
                        {hiddenCount > 0 ? (
                            <CommentsViewModeSelector
                                activeCount={activeCount}
                                hiddenCount={hiddenCount}
                                value={viewMode}
                                onChange={setViewMode}
                            />
                        ) : null}
                        {is.nonEmptyArray(otherLanguages) ? (
                            <CommentLanguagesMenu
                                disabled={newThreadCreationDisabled}
                                languages={otherLanguages}
                                onSwitchLanguage={onSwitchLanguage}
                            />
                        ) : null}
                    </div>
                ) : null}
            </div>
            {viewMode === 'hidden' ? (
                <HiddenCommentsList
                    architectureId={architectureId}
                    commentResolutionDisabled={commentResolutionDisabled}
                    groups={hiddenGroups}
                    itemId={itemId}
                    language={language}
                    version={version}
                />
            ) : (
                <CommentsRail
                    architectureId={architectureId}
                    commentResolutionDisabled={commentResolutionDisabled}
                    itemId={itemId}
                    language={language}
                    newThreadCreationDisabled={newThreadCreationDisabled}
                    version={version}
                />
            )}
        </aside>
    );
};
